import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null, tenant: null });

  const fetchMe = useCallback(async () => {
    const tok = localStorage.getItem("gc_token");
    if (!tok) {
      setState({ status: "anon", user: null, tenant: null });
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setState({ status: "auth", user: data.user, tenant: data.tenant, impersonatedBy: data.impersonated_by });
    } catch {
      localStorage.removeItem("gc_token");
      setState({ status: "anon", user: null, tenant: null });
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async ({ email, password, tenant_slug }) => {
    // SECURITY: Always clear any stale impersonation backup token before login.
    // Stale gc_master_token_backup from a previous master_admin impersonation
    // session must NEVER leak into a regular tenant employee login, otherwise
    // the "Voltar para a Matriz" banner would appear and let funcionários
    // try to access the Master panel.
    localStorage.removeItem("gc_master_token_backup");
    const { data } = await api.post("/auth/login", { email, password, tenant_slug });
    localStorage.setItem("gc_token", data.access_token);
    setState({ status: "auth", user: data.user, tenant: data.tenant, impersonatedBy: null });
    return data;
  };

  const impersonate = async (tenantId) => {
    const { data } = await api.post(`/auth/master/impersonate/${tenantId}`);
    // store the impersonation token as the active token
    localStorage.setItem("gc_master_token_backup", localStorage.getItem("gc_token") || "");
    localStorage.setItem("gc_token", data.access_token);
    // NOTE: caller is responsible for navigation BEFORE calling fetchMe(),
    // otherwise <ProtectedRoute requireRole="master_admin"> will redirect
    // when role changes to tenant_admin.
    return data;
  };

  const exitImpersonation = async () => {
    const back = localStorage.getItem("gc_master_token_backup");
    if (back) {
      localStorage.setItem("gc_token", back);
      localStorage.removeItem("gc_master_token_backup");
      await fetchMe();
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gc_token");
    localStorage.removeItem("gc_master_token_backup");
    setState({ status: "anon", user: null, tenant: null });
  };

  return (
    <AuthCtx.Provider value={{ ...state, login, logout, fetchMe, impersonate, exitImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
